import { type ReactNode } from "react";

import { cn } from "@/lib/utils";

/**
 * One number on a page, with a tone that survives contact with `cn()`.
 *
 * ── The bug this exists to make impossible ──────────────────────────────────
 *
 * Every tile in this codebase was built as
 *
 *     cn("rounded-lg border bg-card px-4 py-2", tone === "danger" && "bg-destructive/5")
 *
 * and `cn()` is `twMerge(clsx(…))`. twMerge resolves conflicting `bg-*`
 * utilities by keeping the LAST one, so `bg-card` is **dropped** and the tile
 * is left with a 5%-alpha wash instead of an opaque surface.
 *
 * On `sites/trade-panel.tsx` that was not cosmetic. Those tiles render outside
 * any `<Card>`, straight onto the shell — and `--color-shell` is `#44403c`, a
 * dark warm grey, **in light mode**. The value text inherits near-black
 * `--color-foreground`. So the OVERDUE tile, the one thing anyone opens a
 * location page to see, was near-black on dark grey in the default theme.
 *
 * ── Why this is a component and not a fixed call site ───────────────────────
 *
 * A tone map appended through `cn()` is the same bug wearing a different hat:
 * the next person to add a tone reintroduces it, and nothing fails. So:
 *
 *   1. The base string carries **no `bg-*` and no colour** — geometry only.
 *      With nothing to conflict with, twMerge has nothing to drop.
 *   2. Every tone names **both** a surface and a foreground. A tone that sets
 *      one and inherits the other is broken on some background, always — and
 *      broken in BOTH directions, as `field-value.tsx` found the hard way when
 *      a fixed tile went white-on-white.
 *   3. The surface is always opaque `bg-card`. The tone lives in the border and
 *      in the VALUE's colour, which keeps the semantic signal without ever
 *      putting translucent paint over an unknown background.
 *
 * `stat-tile.test.tsx` pins the twMerge behaviour itself, so the reasoning
 * above cannot be "simplified" away without a test failing.
 */

export type StatTileTone = "neutral" | "danger" | "warn" | "success" | "info";

interface ToneClasses {
    /** Applied to the tile. ALWAYS names an opaque surface AND a foreground. */
    shell: string;
    /** Applied to the value. Carries the semantic signal. */
    value: string;
}

const TONE: Record<StatTileTone, ToneClasses> = {
    neutral: { shell: "border-border bg-card text-card-foreground", value: "text-card-foreground" },
    danger: { shell: "border-destructive/50 bg-card text-card-foreground", value: "text-destructive" },
    warn: { shell: "border-warning/50 bg-card text-card-foreground", value: "text-warning" },
    success: { shell: "border-success/50 bg-card text-card-foreground", value: "text-success" },
    info: { shell: "border-primary/50 bg-card text-card-foreground", value: "text-primary" },
};

export interface StatTileProps {
    label: ReactNode;
    /** The figure. Omit and pass `children` when the body is not just a value. */
    value?: ReactNode;
    tone?: StatTileTone;
    icon?: ReactNode;
    /** Right-hand affordance on the label row — an Edit button, a link. */
    action?: ReactNode;
    /** Small line under the value: the age of a problem, or why there is none. */
    detail?: ReactNode;
    /** Replaces the value row entirely (the editable due-date tile uses this). */
    children?: ReactNode;
    className?: string;
    title?: string;
}

export function StatTile({
    label,
    value,
    tone = "neutral",
    icon,
    action,
    detail,
    children,
    className,
    title,
}: StatTileProps) {
    const t = TONE[tone];
    return (
        <div
            title={title}
            // Geometry only in the base — see the note above. Anything with a
            // `bg-*` here would be the bug again.
            className={cn("rounded-lg border px-3 py-2 min-w-[110px]", t.shell, className)}
        >
            <div className="flex items-center justify-between gap-1 text-[10px] font-medium uppercase tracking-wide opacity-80">
                <span className="flex items-center gap-1">
                    {icon}
                    {label}
                </span>
                {action}
            </div>
            {children ?? <div className={cn("mt-0.5 text-lg font-semibold tabular-nums", t.value)}>{value}</div>}
            {detail && <div className="text-xs opacity-75">{detail}</div>}
        </div>
    );
}
